import { useAuthState } from '@auth/hooks';
import type { Community } from '@communities/community/domain';
import { routes as communityRoutes } from '@communities/routes';
import { HeroImage, CommunityHeader } from '@core/components';
import { UserRoles, FEATURE_FLAGS } from '@core/domain';
import { useIsFeatureEnabled } from '@core/hooks';
import { withAppInsightsTracking } from '@core/logging/withAppInsightsTracking';
import { routes as auditRoutes } from '@internal-audit/routes';
import { Heading, Button, ButtonType, ButtonSize, OverlayMode } from '@soteria/design-system-ve';
import { useNavigate } from 'react-router-dom';

import * as styles from './CommunityHeaderSection.css.ts';

/**
 * Build the subheader text with community group, content rating, and genres
 */
function buildSubheaderText(community: Community): string {
  const subheaderParts: string[] = [];

  if (community.communityGroup?.name) {
    subheaderParts.push(community.communityGroup.name);
  }

  if (community.optionalMetadata?.contentRating?.description) {
    subheaderParts.push(community.optionalMetadata.contentRating.description);
  }

  if (community.optionalMetadata?.contentGenres && community.optionalMetadata.contentGenres.length > 0) {
    subheaderParts.push(community.optionalMetadata.contentGenres.join(', '));
  }

  return subheaderParts.join(' • ');
}

interface CommunityHeaderSectionProps {
  community: Community;
}

/**
 * Action buttons shared between V1 and V2 headers.
 * Extracted to reduce component complexity.
 */
function CommunityHeaderActions({
  community,
  isOverviewEnabled,
  hasQAAccess,
  hasModerationAccess,
  showCaseSearchButton,
}: {
  community: Community;
  isOverviewEnabled: boolean;
  hasQAAccess: boolean;
  hasModerationAccess: boolean;
  showCaseSearchButton: boolean;
}) {
  const navigate = useNavigate();

  const onTestClassification = () => {
    navigate(communityRoutes.communities.classificationTesting.root(community.id));
  };

  const onViewAudits = () => {
    navigate(auditRoutes.internalAudit.community.audits(community.id));
  };

  const onSearchContent = () => {
    navigate(communityRoutes.communities.contentSearch.root(community.id));
  };

  const onSearchCases = () => {
    navigate(communityRoutes.communities.caseSearch.root(community.id));
  };

  const onModeration = () => {
    navigate(`/communities/${community.id}/moderation`);
  };

  return (
    <>
      {/* Navigation buttons - hidden when Overview tab is enabled (moved to Overview tab) */}
      {!isOverviewEnabled && (
        <>
          {/* Test Classification Button */}
          <Button buttonSize={ButtonSize.Large} buttonType={ButtonType.Primary} overlayMode={OverlayMode.Solid} onClick={onTestClassification}>
            TEST CLASSIFICATION
          </Button>

          {/* View Audits Button - Only show to users with QA access*/}
          {hasQAAccess && (
            <Button buttonSize={ButtonSize.Large} buttonType={ButtonType.Primary} overlayMode={OverlayMode.Solid} onClick={onViewAudits}>
              VIEW AUDITS
            </Button>
          )}

          {/* Search Content Button */}
          <Button buttonSize={ButtonSize.Large} buttonType={ButtonType.Primary} overlayMode={OverlayMode.Solid} onClick={onSearchContent}>
            SEARCH CONTENT
          </Button>

          {/* Search Cases Button - Only show when feature flag is enabled */}
          {showCaseSearchButton && (
            <Button buttonSize={ButtonSize.Large} buttonType={ButtonType.Primary} overlayMode={OverlayMode.Solid} onClick={onSearchCases}>
              SEARCH CASES
            </Button>
          )}

          {/* Moderation Button - Only show when feature flag is enabled and user has moderation permissions */}
          {hasModerationAccess && (
            <Button buttonSize={ButtonSize.Large} buttonType={ButtonType.Primary} overlayMode={OverlayMode.Solid} onClick={onModeration}>
              MODERATION
            </Button>
          )}
        </>
      )}
    </>
  );
}

/**
 * Header section component with gradient transition
 * Contains community name, description, metadata, and action buttons
 * Background image is handled by the global useHeroBackground hook
 */

function CommunityHeaderSectionComponent({ community }: CommunityHeaderSectionProps) {
  const { hasRoleAccess } = useAuthState();

  const { name } = community;

  const isOverviewEnabled = useIsFeatureEnabled(FEATURE_FLAGS.COMMUNITY_OVERVIEW);
  const isModerationEnabled = useIsFeatureEnabled(FEATURE_FLAGS.MODERATION);
  const isCaseSearchEnabled = useIsFeatureEnabled(FEATURE_FLAGS.CASE_SEARCH);

  // Only compute navigation-related state when header buttons are visible
  const hasQAAccess = !isOverviewEnabled && hasRoleAccess(UserRoles.QA, community.communityGroupId, community.id);
  const hasModerationAccess =
    !isOverviewEnabled &&
    isModerationEnabled &&
    (hasRoleAccess(UserRoles.MODERATOR, community.communityGroupId, community.id) || hasRoleAccess(UserRoles.ADMIN, community.communityGroupId, community.id));
  const showCaseSearchButton = !isOverviewEnabled && isCaseSearchEnabled;

  const subheaderText = buildSubheaderText(community);

  // Shared action buttons - used by both V1 and V2
  const actionButtons = (
    <CommunityHeaderActions
      community={community}
      hasModerationAccess={hasModerationAccess}
      hasQAAccess={hasQAAccess}
      isOverviewEnabled={isOverviewEnabled}
      showCaseSearchButton={showCaseSearchButton}
    />
  );

  // V1 component for wrapper
  const v1Component = (
    <div className={styles.heroSection} data-testid="community-header-v1">
      {/* Gradient Overlay */}
      <div className={styles.heroOverlay} />

      <div className={styles.heroContent}>
        {/* Left Column - Image */}
        <HeroImage logoUrl={community.logoUrl} name={name} />

        {/* Right Column - Content */}
        <div className={styles.heroContentRight}>
          {/* Title and Subheader Container */}
          <div>
            {/* Community Title */}
            <Heading className={styles.communityTitle} level="h1">
              {name}
            </Heading>

            {/* Community Subheader with Group, Rating, and Genres */}
            {subheaderText && <div className={styles.communitySubheader}>{subheaderText}</div>}
          </div>

          {/* Action Buttons */}
          <div className={styles.actionsContainer}>{actionButtons}</div>
        </div>
      </div>
    </div>
  );

  return (
    <CommunityHeader
      v1Component={v1Component}
      v2Children={actionButtons}
      v2Props={{
        title: name,
        subtitle: subheaderText || undefined,
        'data-testid': 'community-header-v2',
      }}
    />
  );
}

export const CommunityHeaderSection = withAppInsightsTracking(CommunityHeaderSectionComponent, 'CommunityHeaderSection');
